import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Button, CircularProgress, useTheme, Grid } from '@mui/material';

import FieldRow from 'components/FormElements/FieldRow';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import { AppSimpleSelect } from 'components/FormElements';
import { useGetRole } from 'hooks/ui/useGetRole';
import { FirmSelector } from 'components/FirmSelector';
import { numberToDisplayString } from 'further-ui/utils';
import { createShareSplitAddRoute } from 'adminConstants/routes';
import AppNumberInput from 'components/FormElements/AppNumberInput';
import DistinctLegalCompanyNameSelect from 'components/DistinctLegalCompanyNameSelect';
import { useNotification } from 'hooks/ui/useNotification';
import { ConsiderationType, ExitCompany } from 'further-types/exit';
import CashConsiderationFields from './CashConsiderationFields';
import ShareConsiderationFields from './ShareConsiderationFields';
import calculateShareConsiderationValues from './calculate-share-consideration-values';
import {
  handleCalculateCashConsiderationValuesTaxable,
  handleCalculateCashConsiderationValuesReceived,
} from './handle-calculate-cash-consideration-values';
import api from 'lib/trpcClient';
import ShareClassMultiSelect from 'components/ShareClassMultiSelect';
import { format, addDays } from 'date-fns';
import ButtonGroup from 'components/ButtonGroup';
import GridContainer from 'components/GridContainer';
import AllocationSelector from 'components/AllocationSelector';
import Spacing from 'components/Spacing';
import { styled } from '@mui/material';
import { useAllocations } from 'hooks/data/allocation/useAllocations';
import getAllocationLabel from 'utils/allocationLabel';
import { Controller } from 'react-hook-form';
import { useExitDetailsForm } from './hooks/useExitDetailsForm';
import { useFormRestoration } from './hooks/useFormRestoration';
import { useExitDetailsFormValidation } from './hooks/useExitDetailsFormValidation';
import {
  buildEditModeSubmissionData,
  buildCreateModeSubmissionData,
} from './helpers/build-exit-submission-data';
import { ExitDetailsSubmitData, PreviousExitDetails } from './types';
import {
  ShareConsiderationAllocationMethod,
  TaxReliefElegibleType,
} from 'further-types/exit';

const Row = styled('div')(() => ({
  display: 'flex',
  maxWidth: '20rem',
  gridGap: '1rem',
  '& > div': {
    width: '100%',
  },
}));

const Li = styled('li')(() => ({
  marginLeft: '1rem',
}));

const StyledRow = styled('div')(() => ({
  display: 'flex',
  minHeight: '42px',
  alignItems: 'center',
  gridGap: '1rem',

  '& > div': {
    width: '100%',
  },
}));

const StyledRowWithDynamicHeight = styled('div')<{ hasError?: boolean }>(
  ({ hasError }) => ({
    display: 'flex',
    height: hasError ? 'auto' : '42px',
    alignItems: hasError ? 'flex-start' : 'center',
    gridGap: '1rem',

    '& > div': {
      width: '100%',
    },
  }),
);

const StyledShareCountUnknown = styled('span')(({ theme }) => ({
  fontStyle: 'italic',
  color: theme.palette.text.greyLabel,
}));

const StyledInputFix = styled('div')(() => ({
  '& .MuiInputBase-input': {
    height: 'auto',
  },
}));

const StyledFieldRow = styled(FieldRow)(() => ({
  minHeight: '42px',
}));

const StyledShareClassesList = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
}));
type ExitDetailsFormProps = {
  onSubmit: (details: ExitDetailsSubmitData) => void;
  exitDetailsStepCompleted: boolean;
  previousExitDetails?: PreviousExitDetails & { originalExitDate?: Date };
  isBeingEdited?: boolean;
  exitId?: string;
  isBeingCorrected?: boolean;
  isCorrectingAmendment?: boolean;
};

export type ExitDetailsFormValues = {
  firmId: string;
  firmName: string;
  exitDate: Date;
  selectedCompany: ExitCompany;
  sharesToBeSold: number;
  sharePrice: string;
  taxableValueSharePrice: string;
  taxableValueTotal: string;
  receivedValueTotal: string;
  isReceivedValueSharePriceEdited: boolean;
  chargeAccruedFees: boolean | undefined;
  selectedShareClasses: Array<string>;
  companyIds: Array<string>;
  selectedAllocations: Array<string>;
  shareAllocationUploadIds: Array<string>;
  taxReliefElegibleAllocationIds: Array<string>;
  originalTaxReliefElegible?: TaxReliefElegibleType;
  originalTaxableValueSharePrice?: string;
  considerationType: ConsiderationType | undefined;
  shareConsiderationCompanyId?: string;
  shareConsiderationShareClassId?: string;
  shareConsiderationShareClassIds?: Array<string>;
  totalConsiderationShares: string;
  shareConsiderationValuePerShare: string;
  shareConsiderationValueTotal: string;
  shareConsiderationEisEligible: boolean | undefined;
  isInitialConsideration?: boolean;
  shareConsiderationAllocationMethod:
    | ShareConsiderationAllocationMethod
    | undefined;
  permitPartialShareAllocation: boolean;
  taxReliefElegible?: TaxReliefElegibleType;
};

const ExitDetailsForm: React.FC<ExitDetailsFormProps> = ({
  onSubmit,
  exitDetailsStepCompleted,
  previousExitDetails,
  isBeingEdited = false,
  exitId,
  isBeingCorrected = false,
  isCorrectingAmendment = false,
}) => {
  const theme = useTheme();
  const { isSuperAdmin, firmId: usersFirmId } = useGetRole();
  const isEditMode = isBeingEdited || isBeingCorrected;
  const notification = useNotification();
  const apiUtils = api.useUtils();
  const lastManuallyEditedField = useRef<
    'perShare' | 'total' | 'shares' | null
  >(null);
  const lastManuallyEditedFieldTaxable = useRef<'perShare' | 'total' | null>(
    null,
  );
  const lastManuallyEditedFieldReceived = useRef<'perShare' | 'total' | null>(
    null,
  );
  const isCalculating = useRef<boolean>(false);
  const isCalculatingCashTaxable = useRef<boolean>(false);
  const isCalculatingCashReceived = useRef<boolean>(false);

  // Use extracted form setup hook
  const { form, initialCorrectionValues } = useExitDetailsForm(
    isEditMode,
    previousExitDetails,
    usersFirmId,
  );

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = form;

  // Use extracted form restoration hook
  useFormRestoration(
    form,
    isEditMode,
    previousExitDetails,
    isBeingCorrected,
    exitDetailsStepCompleted,
    usersFirmId,
    initialCorrectionValues,
  );

  // Watch form values used in JSX conditionals
  const firmId = watch('firmId');
  const exitDate = watch('exitDate');
  const selectedCompany = watch('selectedCompany');
  const sharesToBeSold = watch('sharesToBeSold');
  const isReceivedValueSharePriceEdited = watch(
    'isReceivedValueSharePriceEdited',
  );
  const taxReliefElegible = watch('taxReliefElegible');
  const selectedShareClasses = watch('selectedShareClasses');
  const selectedAllocations = watch('selectedAllocations');
  const taxReliefElegibleAllocationIds = watch(
    'taxReliefElegibleAllocationIds',
  );
  const considerationType = watch('considerationType');
  const shareConsiderationCompanyId = watch('shareConsiderationCompanyId');
  const totalConsiderationShares = watch('totalConsiderationShares');
  const shareConsiderationValuePerShare = watch(
    'shareConsiderationValuePerShare',
  );
  const shareConsiderationValueTotal = watch('shareConsiderationValueTotal');
  const shareConsiderationAllocationMethod = watch(
    'shareConsiderationAllocationMethod',
  );
  const taxableValueSharePrice = watch('taxableValueSharePrice');

  // When correcting an amendment, compare against the previous exit's taxable value
  const baselineTaxableValueSharePrice =
    isBeingCorrected && isCorrectingAmendment
      ? previousExitDetails?.originalTaxableValueSharePrice
      : previousExitDetails?.taxableValueSharePrice;

  const hasTaxableValueIncrease = isEditMode
    ? Number(taxableValueSharePrice) >
      Number(baselineTaxableValueSharePrice ?? 0)
    : false;

  // In edit/correction mode, default S/EIS status to No when taxable value exceeds baseline
  useEffect(() => {
    if (isEditMode && hasTaxableValueIncrease) {
      setValue('taxReliefElegible', TaxReliefElegibleType.No);
    }
  }, [isEditMode, hasTaxableValueIncrease, taxableValueSharePrice, setValue]);

  const isInitialConsideration = watch('isInitialConsideration');

  // Set default for isInitialConsideration based on mode and date (only once on mount)
  useEffect(() => {
    // Skip if a persisted value exists or if not in share consideration mode
    if (
      considerationType !== ConsiderationType.Share ||
      isInitialConsideration !== undefined
    ) {
      return;
    }

    // Skip if in edit mode but don't yet have the previous details loaded
    if (isEditMode && !previousExitDetails) {
      return;
    }

    let defaultValue = false; // Create mode: default to false

    if (isEditMode && previousExitDetails?.originalExitDate) {
      // Edit mode: true if within 90 days of original exit, false otherwise
      const originalDate = new Date(previousExitDetails.originalExitDate);
      const ninetyDaysLater = addDays(originalDate, 90);
      defaultValue = exitDate <= ninetyDaysLater;
    }

    setValue('isInitialConsideration', defaultValue);
    // Only run once when form initializes with share consideration (not on exitDate changes)
  }, [
    considerationType,
    isInitialConsideration,
    isEditMode,
    previousExitDetails,
    exitDate,
    setValue,
  ]);

  // When correcting, company / share classes / allocations are locked; shares to be sold and fields below remain editable
  const lockCompanyShareClassAllocations = isEditMode || isBeingCorrected;

  // Local state for computed/derived values
  const [maxSharesAvailableForSale, setMaxSharesAvailableForSale] =
    useState<number>();
  const [
    maxSharesAvailableForSaleLoading,
    setMaxSharesAvailableForSaleLoading,
  ] = useState(false);

  // For edit/correction mode: fetch allocations to display (read-only)
  // For correction mode: include fully exited subscriptions from this exit
  const { data: allocationsData, isLoading: isLoadingAllocations } =
    useAllocations({
      params: {
        firmId: firmId ?? '',
        companyIds:
          selectedCompany?._id && typeof selectedCompany._id === 'string'
            ? [selectedCompany._id]
            : [],
        ...(isBeingCorrected && exitId && { includeExitId: exitId }),
      },
      enabled:
        lockCompanyShareClassAllocations &&
        !!selectedCompany &&
        !!firmId &&
        !!previousExitDetails?.selectedCompany,
    });

  const allocations = allocationsData?.uploadShareHoldings.map(
    (allocation) => ({
      _id: allocation._id,
      allocationName: getAllocationLabel(allocation),
    }),
  );

  // For edit/correction mode: fetch share classes to display (read-only)
  const { data: shareClassesData } = api.investee.listShareClasses.useQuery(
    {
      investeeId: selectedCompany?._id ?? '',
    },
    {
      enabled:
        lockCompanyShareClassAllocations &&
        !!selectedCompany &&
        !!selectedCompany?._id &&
        !!previousExitDetails?.selectedShareClasses?.length,
    },
  );

  const shareClasses = shareClassesData?.result
    .filter((shareClass) =>
      previousExitDetails?.selectedShareClasses?.includes(
        shareClass.investeeId,
      ),
    )
    .map((shareClass) => ({
      _id: shareClass.investeeId,
      shareClassName: shareClass.shareClass || 'Ordinary',
    }));

  // Query for exit companies - only those with current shareholdings
  const { data: uniqueCompaniesByLegalName } =
    api.investee.listUniqueInvestees.useQuery(
      {
        firmId: firmId ?? '',
        currentShareholdingsOnly: true,
      },
      {
        enabled: !!firmId,
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    );

  // Query for share consideration companies - all companies in the firm
  const { data: allCompaniesForShareConsideration } =
    api.investee.listUniqueInvestees.useQuery(
      {
        firmId: firmId ?? '',
        currentShareholdingsOnly: false,
      },
      {
        enabled: !!firmId,
        staleTime: 0,
        refetchOnWindowFocus: true,
      },
    );

  // Get the legal name of the company being exited
  const exitingCompany = uniqueCompaniesByLegalName?.result?.find(
    (investee) => investee._id === selectedCompany?._id,
  );
  const exitingCompanyLegalName = exitingCompany?.legalName;

  // Filter out companies with the same legal name as the exiting company
  // Use allCompaniesForShareConsideration for share consideration dropdown
  // When correcting, ensure the existing share consideration company is included
  const filteredConsiderationCompanies =
    allCompaniesForShareConsideration?.result?.filter((investee) => {
      // Always include the company if it's the currently selected share consideration company
      if (
        isBeingCorrected &&
        previousExitDetails?.shareConsiderationCompanyId &&
        investee._id === previousExitDetails.shareConsiderationCompanyId
      ) {
        return true;
      }
      // Otherwise, filter out companies with same legal name as exiting company
      return investee.legalName !== exitingCompanyLegalName;
    }) || [];

  const disableEdit = exitDetailsStepCompleted;

  // Set values from previousExitDetails in edit mode
  useEffect(() => {
    if (isEditMode && previousExitDetails) {
      const companyId =
        previousExitDetails.selectedCompany &&
        typeof previousExitDetails.selectedCompany === 'object' &&
        '_id' in previousExitDetails.selectedCompany
          ? previousExitDetails.selectedCompany._id
          : previousExitDetails.selectedCompany;

      const shareClassId = previousExitDetails.shareConsiderationShareClassIds
        ? Array.isArray(previousExitDetails.shareConsiderationShareClassIds) &&
          previousExitDetails.shareConsiderationShareClassIds.length > 0
          ? previousExitDetails.shareConsiderationShareClassIds[0]
          : typeof previousExitDetails.shareConsiderationShareClassIds ===
            'string'
          ? previousExitDetails.shareConsiderationShareClassIds
          : undefined
        : undefined;

      reset({
        firmId: previousExitDetails.firmId || '',
        exitDate: previousExitDetails.exitDate
          ? previousExitDetails.exitDate instanceof Date
            ? previousExitDetails.exitDate
            : new Date(previousExitDetails.exitDate)
          : new Date(),
        selectedCompany: {
          _id: companyId,
          legalName: previousExitDetails.selectedCompany?.legalName,
          tradingName: previousExitDetails.selectedCompany?.tradingName,
        },
        sharesToBeSold: previousExitDetails.sharesToBeSold || 0,
        sharePrice: previousExitDetails.sharePrice?.toString() || '0',
        taxableValueSharePrice:
          previousExitDetails.taxableValueSharePrice?.toString() || '0',
        isReceivedValueSharePriceEdited: false,
        chargeAccruedFees: previousExitDetails.chargeAccruedFees,
        taxReliefElegible: previousExitDetails.taxReliefElegible,
        selectedShareClasses: previousExitDetails.selectedShareClasses || [],
        selectedAllocations: previousExitDetails.shareAllocationUploadIds || [],
        taxReliefElegibleAllocationIds:
          previousExitDetails.taxReliefElegibleAllocationIds || [],
        considerationType: previousExitDetails.considerationType,
        shareConsiderationCompanyId:
          previousExitDetails.shareConsiderationCompanyId,
        shareConsiderationShareClassId: shareClassId,
        totalConsiderationShares:
          previousExitDetails.totalConsiderationShares?.toString() || '0',
        shareConsiderationValuePerShare:
          previousExitDetails.shareConsiderationValuePerShare?.toString() ||
          '0',
        shareConsiderationValueTotal:
          previousExitDetails.shareConsiderationValueTotal?.toString() || '0',
        shareConsiderationEisEligible:
          previousExitDetails.shareConsiderationEisEligible,
        isInitialConsideration: previousExitDetails.isInitialConsideration,
        shareConsiderationAllocationMethod:
          previousExitDetails.shareConsiderationAllocationMethod ===
            ShareConsiderationAllocationMethod.Upload ||
          previousExitDetails.shareConsiderationAllocationMethod ===
            ShareConsiderationAllocationMethod.Automatic
            ? previousExitDetails.shareConsiderationAllocationMethod
            : undefined,
        permitPartialShareAllocation:
          previousExitDetails.permitPartialShareAllocation ?? false,
      });
    }
  }, [isEditMode, previousExitDetails, reset]);

  // Auto-reset taxReliefElegible when taxable value falls below previous value
  useEffect(() => {
    if (
      isEditMode &&
      !hasTaxableValueIncrease &&
      previousExitDetails?.taxReliefElegible
    ) {
      setValue('taxReliefElegible', previousExitDetails.taxReliefElegible);
      setValue(
        'taxReliefElegibleAllocationIds',
        previousExitDetails.taxReliefElegibleAllocationIds ?? [],
      );
    }
  }, [isEditMode, hasTaxableValueIncrease, previousExitDetails, setValue]);

  // Edit/correction mode: allocation display helpers
  const noAllocationsSelected =
    lockCompanyShareClassAllocations &&
    !previousExitDetails?.shareAllocationUploadIds?.length;

  const hasAllocationsToShow =
    lockCompanyShareClassAllocations &&
    (previousExitDetails?.shareAllocationUploadIds?.length ?? 0) > 0;

  useEffect(() => {
    const fetchMaxSharesAvailableForSale = async () => {
      // Fetch total shares available for both create (when allocations selected) and edit/correction (from prepopulated allocations)
      if (
        selectedShareClasses.length > 0 &&
        selectedAllocations.length > 0 &&
        exitDate &&
        firmId
      ) {
        setMaxSharesAvailableForSale(undefined);
        setMaxSharesAvailableForSaleLoading(true);

        try {
          const sumSharesHeldOnDateData =
            await apiUtils.exit.sumSharesHeldOnDate.fetch({
              firmId,
              companyIds: selectedShareClasses,
              exitDate: format(exitDate, 'dd/MM/yyyy'),
              shareAllocationUploadIds: selectedAllocations,
              ...(exitId && (isBeingCorrected || isEditMode) ? { exitId } : {}),
            });

          setMaxSharesAvailableForSale(sumSharesHeldOnDateData ?? 0);
        } catch (error: any) {
          notification.error(
            error?.data?.cause ??
              error?.message ??
              'Error fetching shares held on date.',
          );
        } finally {
          setMaxSharesAvailableForSaleLoading(false);
        }
      }
    };
    void fetchMaxSharesAvailableForSale();
  }, [
    selectedAllocations,
    selectedShareClasses,
    exitDate,
    firmId,
    isBeingCorrected,
    exitId,
  ]);

  // Wrapper function to call the extracted helper with current state
  const handleCalculateShareConsiderationValues = (
    changedField: 'perShare' | 'total' | 'shares',
    newValue?: string,
  ) => {
    calculateShareConsiderationValues({
      changedField,
      newValue,
      considerationType,
      totalConsiderationShares,
      shareConsiderationValuePerShare,
      shareConsiderationValueTotal,
      isCalculating,
      setValue,
    });
  };

  const onCalculateCashConsiderationValuesTaxable = (
    changedField: 'perShare' | 'total',
    newValue?: string,
  ) => {
    handleCalculateCashConsiderationValuesTaxable({
      changedField,
      newValue,
      considerationType,
      sharesToBeSold,
      isCalculating: isCalculatingCashTaxable,
      setValue,
    });
  };

  const onCalculateCashConsiderationValuesReceived = (
    changedField: 'perShare' | 'total',
    newValue?: string,
  ) => {
    handleCalculateCashConsiderationValuesReceived({
      changedField,
      newValue,
      considerationType,
      sharesToBeSold,
      isCalculating: isCalculatingCashReceived,
      setValue,
    });
  };

  // When number of shares changes, recalculate totals from current per-share values
  useEffect(() => {
    if (
      considerationType !== ConsiderationType.Cash ||
      !sharesToBeSold ||
      sharesToBeSold <= 0
    ) {
      return;
    }
    const taxablePerShare = getValues('taxableValueSharePrice');
    const receivedPerShare = getValues('sharePrice');
    onCalculateCashConsiderationValuesTaxable('perShare', taxablePerShare);
    onCalculateCashConsiderationValuesReceived('perShare', receivedPerShare);
  }, [sharesToBeSold, considerationType]);

  // Use extracted validation hook
  const { validateForm } = useExitDetailsFormValidation(
    setError,
    clearErrors,
    isEditMode,
    isBeingCorrected,
    previousExitDetails,
    maxSharesAvailableForSale,
  );

  const onSubmitForm = (data: ExitDetailsFormValues) => {
    if (!validateForm(data)) {
      return;
    }

    const submitData = isEditMode
      ? buildEditModeSubmissionData(
          data,
          previousExitDetails,
          isBeingCorrected,
          initialCorrectionValues.current,
        )
      : buildCreateModeSubmissionData(data);

    onSubmit(submitData);
  };

  return (
    <GridContainer>
      <Grid size={{ xs: 12, md: 12, lg: 8 }}>
        <Spacing>
          {isSuperAdmin && (
            <StyledFieldRow title="Firm" centerTitle>
              <StyledRow>
                {lockCompanyShareClassAllocations ? (
                  <Row>{previousExitDetails?.firmName}</Row>
                ) : (
                  <FirmSelector
                    firmId={firmId}
                    onChange={(value) => {
                      if (firmId && !isEditMode) {
                        setValue('selectedCompany', {
                          _id: '',
                          legalName: '',
                          tradingName: '',
                        });
                        setValue('selectedShareClasses', []);
                        setValue('selectedAllocations', []);
                      }
                      setValue('firmId', value || '');
                    }}
                    disabled={disableEdit}
                  />
                )}
              </StyledRow>
            </StyledFieldRow>
          )}
          <StyledFieldRow
            title={
              isCorrectingAmendment || (isEditMode && !isBeingCorrected)
                ? 'Exit update date'
                : 'Exit date'
            }
            centerTitle
          >
            <StyledRow>
              <Controller
                name="exitDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppDatePicker
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    disableFuture={true}
                    minDate={
                      isCorrectingAmendment &&
                      previousExitDetails?.originalExitDate
                        ? previousExitDetails.originalExitDate
                        : undefined
                    }
                    helperText={error?.message}
                    error={!!error}
                    disabled={disableEdit}
                  />
                )}
              />
            </StyledRow>
          </StyledFieldRow>
          <StyledFieldRow title="Company" centerTitle>
            <StyledRow>
              {lockCompanyShareClassAllocations ? (
                <Row>{previousExitDetails?.selectedCompany?.tradingName}</Row>
              ) : firmId ? (
                <Controller
                  name="selectedCompany"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const handleCompanyChange = (
                      companyId: string | undefined,
                    ) => {
                      if (companyId) {
                        const selectedCompanyData =
                          uniqueCompaniesByLegalName?.result?.find(
                            (investee) => investee._id === companyId,
                          );
                        if (selectedCompanyData) {
                          const companyObject = {
                            _id: selectedCompanyData._id,
                            legalName: selectedCompanyData.legalName,
                            tradingName: selectedCompanyData.tradingName,
                          };
                          // Check if this is a different company than the current one
                          // In edit/correction mode, don't clear share classes during form restoration
                          if (
                            !field.value?._id ||
                            field.value._id !== companyObject._id
                          ) {
                            if (!isEditMode) {
                              setValue('selectedShareClasses', []);
                              setValue('selectedAllocations', []);
                            }
                          }
                          field.onChange(companyObject);
                        }
                      } else {
                        // Clear the field but maintain the structure
                        // In edit/correction mode, preserve share classes during form restoration
                        if (!isEditMode) {
                          setValue('selectedShareClasses', []);
                          setValue('selectedAllocations', []);
                        }
                        field.onChange({
                          _id: '',
                          legalName: '',
                          tradingName: '',
                        });
                      }
                    };

                    return (
                      <DistinctLegalCompanyNameSelect
                        firmId={firmId}
                        value={field.value?._id ?? ''}
                        handleChangeCallback={handleCompanyChange}
                        error={!!error}
                        helperText={error?.message}
                        disabled={disableEdit}
                        currentShareholdingsOnly
                      />
                    );
                  }}
                />
              ) : (
                <StyledShareCountUnknown>
                  First select a firm
                </StyledShareCountUnknown>
              )}
            </StyledRow>
          </StyledFieldRow>
          {!lockCompanyShareClassAllocations && (
            <>
              <StyledFieldRow title="Share class(es)" centerTitle>
                <StyledRow>
                  {firmId && selectedCompany ? (
                    <Controller
                      name="selectedShareClasses"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <ShareClassMultiSelect
                          firmId={firmId}
                          selectedCompanyId={selectedCompany._id ?? ''}
                          value={field.value}
                          onChange={(companyIds) => {
                            field.onChange(companyIds);
                            if (companyIds.length !== field.value.length) {
                              setValue('selectedAllocations', []);
                            }
                          }}
                          error={!!error}
                          helperText={error?.message}
                          disabled={disableEdit}
                          currentShareholdingsOnly
                        />
                      )}
                    />
                  ) : (
                    <StyledShareCountUnknown>
                      First select a firm and a company
                    </StyledShareCountUnknown>
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow title="Allocations" centerTitle>
                <StyledRow>
                  {firmId && selectedShareClasses.length > 0 ? (
                    <Controller
                      name="selectedAllocations"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <AllocationSelector
                          firmId={firmId}
                          companyIds={selectedShareClasses}
                          value={field.value}
                          onChange={field.onChange}
                          error={!!error}
                          helperText={error?.message}
                          disabled={disableEdit || !selectedShareClasses.length}
                          currentShareholdingsOnly
                          endDate={exitDate}
                        />
                      )}
                    />
                  ) : (
                    <StyledShareCountUnknown>
                      First select a firm and share class(es)
                    </StyledShareCountUnknown>
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow title="Total shares" centerTitle>
                <StyledRow>
                  {!selectedCompany ? (
                    <StyledShareCountUnknown>
                      First select a company
                    </StyledShareCountUnknown>
                  ) : maxSharesAvailableForSaleLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    numberToDisplayString(maxSharesAvailableForSale ?? 0, {
                      maximumFractionDigits: 10,
                    })
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow
                title="Shares to be sold"
                centerTitle
                tooltipText={
                  <span>
                    If there has been a share split between the company's last
                    share price update and this exit, please update this in the
                    share split function for this company{' '}
                    {selectedCompany ? (
                      <NavLink
                        style={{ color: theme.palette.primary[400] }}
                        target="_blank"
                        to={createShareSplitAddRoute(selectedCompany._id ?? '')}
                      >
                        here
                      </NavLink>
                    ) : (
                      ''
                    )}{' '}
                    before processing an exit.
                  </span>
                }
              >
                <StyledRowWithDynamicHeight hasError={!!errors.sharesToBeSold}>
                  <Controller
                    name="sharesToBeSold"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <StyledInputFix>
                        <AppNumberInput
                          value={field.value}
                          error={!!error}
                          helperText={error?.message}
                          disabled={disableEdit}
                          min={0}
                          max={maxSharesAvailableForSale}
                          decimals={10}
                          onChange={(value) => field.onChange(value)}
                        />
                      </StyledInputFix>
                    )}
                  />

                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={disableEdit}
                    onClick={() =>
                      setValue('sharesToBeSold', maxSharesAvailableForSale ?? 0)
                    }
                  >
                    All
                  </Button>
                </StyledRowWithDynamicHeight>
              </StyledFieldRow>
            </>
          )}
          {lockCompanyShareClassAllocations && (
            <>
              <StyledFieldRow title="Share class(es)">
                <StyledRow>
                  {shareClasses && shareClasses.length > 0 ? (
                    <StyledShareClassesList>
                      {shareClasses.map((shareClass) => (
                        <div key={shareClass._id}>
                          {shareClass.shareClassName}
                        </div>
                      ))}
                    </StyledShareClassesList>
                  ) : (
                    <span>Loading...</span>
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow title="Allocations" centerTitle>
                <StyledRow>
                  {hasAllocationsToShow && !isLoadingAllocations && (
                    <ul>
                      {allocations
                        ?.filter((allocation) =>
                          previousExitDetails?.shareAllocationUploadIds?.includes(
                            allocation._id,
                          ),
                        )
                        .map((allocation) => (
                          <Li key={allocation._id}>
                            {allocation.allocationName}
                          </Li>
                        ))}
                    </ul>
                  )}
                  {isLoadingAllocations ? (
                    <span>Loading...</span>
                  ) : (
                    !hasAllocationsToShow &&
                    noAllocationsSelected && (
                      <span>All allocations selected</span>
                    )
                  )}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow title="Total shares" centerTitle>
                <StyledRow>
                  {numberToDisplayString(maxSharesAvailableForSale ?? 0, {
                    maximumFractionDigits: 10,
                  })}
                </StyledRow>
              </StyledFieldRow>
              <StyledFieldRow
                title={
                  isCorrectingAmendment ? 'Shares sold' : 'Shares to be sold'
                }
                centerTitle
              >
                <StyledRow>
                  {isCorrectingAmendment ? (
                    numberToDisplayString(
                      Number(previousExitDetails?.sharesToBeSold ?? 0),
                      { maximumFractionDigits: 10 },
                    )
                  ) : isBeingCorrected ? (
                    <StyledRowWithDynamicHeight
                      hasError={!!errors.sharesToBeSold}
                    >
                      <Controller
                        name="sharesToBeSold"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <StyledInputFix>
                            <AppNumberInput
                              value={field.value}
                              error={!!error}
                              helperText={error?.message}
                              disabled={disableEdit}
                              min={0}
                              max={maxSharesAvailableForSale}
                              decimals={10}
                              onChange={(value) => field.onChange(value)}
                            />
                          </StyledInputFix>
                        )}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={disableEdit}
                        onClick={() =>
                          setValue(
                            'sharesToBeSold',
                            maxSharesAvailableForSale ?? 0,
                          )
                        }
                      >
                        All
                      </Button>
                    </StyledRowWithDynamicHeight>
                  ) : (
                    numberToDisplayString(
                      Number(previousExitDetails?.sharesToBeSold || 0),
                      { maximumFractionDigits: 10 },
                    )
                  )}
                </StyledRow>
              </StyledFieldRow>
            </>
          )}

          <StyledFieldRow
            title="Consideration type"
            centerTitle
            tooltipMaxWidth={320}
            tooltipText={
              <span>
                If this exit transaction has both cash and share consideration:
                <ol
                  style={{
                    marginTop: '0.5rem',
                    marginBottom: 0,
                    paddingLeft: '1.5rem',
                  }}
                >
                  <li>
                    Select all selling share classes now, but process either the
                    cash or share consideration component first.
                  </li>
                  <li>
                    Once the first component is processed, update this exit (via
                    the Exits table), to process the other component.
                  </li>
                  <li>
                    If there is more than one class of share consideration being
                    issued in this exit (e.g. Ordinary and A Ordinary shares),
                    process each consideration share class as a separate update
                    (not as a new exit).
                  </li>
                </ol>
              </span>
            }
          >
            <StyledRow>
              <Controller
                name="considerationType"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <AppSimpleSelect
                    fullWidth
                    value={field.value}
                    onChange={(e) => {
                      const newConsiderationType = e.target
                        .value as ConsiderationType;
                      field.onChange(newConsiderationType);

                      // Clear errors for fields that are no longer relevant
                      if (newConsiderationType === ConsiderationType.Cash) {
                        clearErrors('shareConsiderationCompanyId');
                        clearErrors('shareConsiderationShareClassId');
                        clearErrors('totalConsiderationShares');
                        clearErrors('shareConsiderationValuePerShare');
                        clearErrors('shareConsiderationValueTotal');
                      } else if (
                        newConsiderationType === ConsiderationType.Share
                      ) {
                        clearErrors('sharePrice');
                        clearErrors('taxableValueSharePrice');
                        clearErrors('chargeAccruedFees');
                      }
                    }}
                    options={[
                      {
                        value: ConsiderationType.Cash,
                        label: 'Cash consideration',
                      },
                      {
                        value: ConsiderationType.Share,
                        label: 'Share consideration',
                      },
                    ]}
                    placeholder="Select consideration type"
                    disabled={disableEdit || isBeingCorrected}
                    error={!!error}
                    errorMessage={error?.message}
                  />
                )}
              />
            </StyledRow>
          </StyledFieldRow>
          <CashConsiderationFields
            control={control}
            setValue={setValue}
            errors={errors}
            isEditMode={isEditMode}
            disableEdit={disableEdit}
            considerationType={considerationType}
            totalConsiderationShares={totalConsiderationShares}
            taxReliefElegible={taxReliefElegible}
            isReceivedValueSharePriceEdited={isReceivedValueSharePriceEdited}
            isBeingCorrected={isBeingCorrected}
            isCorrectingAmendment={isCorrectingAmendment}
            firmId={firmId}
            selectedShareClasses={selectedShareClasses}
            selectedAllocations={selectedAllocations}
            taxReliefElegibleAllocationIds={taxReliefElegibleAllocationIds}
            previousExitDetails={previousExitDetails}
            sharesToBeSold={sharesToBeSold}
            lastManuallyEditedFieldTaxable={lastManuallyEditedFieldTaxable}
            lastManuallyEditedFieldReceived={lastManuallyEditedFieldReceived}
            calculateCashConsiderationValuesTaxable={
              onCalculateCashConsiderationValuesTaxable
            }
            calculateCashConsiderationValuesReceived={
              onCalculateCashConsiderationValuesReceived
            }
            hasTaxableValueIncrease={hasTaxableValueIncrease}
            originalTaxReliefElegible={
              previousExitDetails?.originalTaxReliefElegible
            }
          />
          <ShareConsiderationFields
            control={control}
            setValue={setValue}
            errors={errors}
            isEditMode={isEditMode}
            disableEdit={disableEdit}
            considerationType={considerationType}
            shareConsiderationCompanyId={shareConsiderationCompanyId}
            totalConsiderationShares={totalConsiderationShares}
            shareConsiderationAllocationMethod={
              shareConsiderationAllocationMethod
            }
            taxReliefElegible={taxReliefElegible}
            firmId={firmId}
            selectedShareClasses={selectedShareClasses}
            selectedAllocations={selectedAllocations}
            taxReliefElegibleAllocationIds={taxReliefElegibleAllocationIds}
            filteredConsiderationCompanies={filteredConsiderationCompanies}
            previousExitDetails={previousExitDetails}
            lastManuallyEditedField={lastManuallyEditedField}
            calculateShareConsiderationValues={
              handleCalculateShareConsiderationValues
            }
            isBeingCorrected={isBeingCorrected}
            isCorrectingAmendment={isCorrectingAmendment}
            hasTaxableValueIncrease={hasTaxableValueIncrease}
            originalTaxReliefElegible={
              previousExitDetails?.originalTaxReliefElegible
            }
          />
        </Spacing>
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 8 }}>
        <Spacing>
          <GridContainer>
            <Grid size={{ md: 10, xs: 12 }} />
            <Grid size={{ md: 2, xs: 12 }}>
              {!disableEdit && (
                <ButtonGroup>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFormSubmit(onSubmitForm)}
                  >
                    Next
                  </Button>
                </ButtonGroup>
              )}
            </Grid>
          </GridContainer>
        </Spacing>
      </Grid>
    </GridContainer>
  );
};

export default ExitDetailsForm;
